import React, { useState, useEffect } from 'react';
import './AddMaterialOrderModal.css';

const AddMaterialOrderModal = ({ open, onClose, onOrderAdded, editOrder }) => {
  const [formData, setFormData] = useState({
    orderNumber: '',
    supplierId: '',
    expectedDate: '',
    notes: '',
    orderItems: []
  });
  
  const [suppliers, setSuppliers] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchMaterialTypes();
      if (editOrder) {
        setFormData({
          orderNumber: editOrder.orderNumber,
          supplierId: editOrder.supplierId,
          expectedDate: editOrder.expectedDate ? editOrder.expectedDate.split('T')[0] : '',
          notes: editOrder.notes || '',
          orderItems: editOrder.orderItems || []
        });
      } else {
        setFormData({
          orderNumber: '',
          supplierId: '',
          expectedDate: '',
          notes: '',
          orderItems: []
        });
      }
      setError('');
    }
  }, [open, editOrder]);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/suppliers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuppliers(data);
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchMaterialTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-types`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMaterialTypes(data);
      }
    } catch (error) {
      console.error('Error fetching material types:', error);
    }
  };

  const getFilteredMaterialTypes = () => {
    if (!formData.supplierId) return [];
    return materialTypes.filter(mt => mt.supplierId === formData.supplierId);
  };

  const addOrderItem = () => {
    const newItem = {
      materialTypeId: '',
      length: '',
      unitPrice: '',
      totalPrice: 0,
      widths: [{
        width: '',
        quantity: 1,
        wtNumber: ''
      }]
    };
    setFormData(prev => ({
      ...prev,
      orderItems: [...prev.orderItems, newItem]
    }));
  };

  const removeOrderItem = (index) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.filter((_, i) => i !== index)
    }));
  };

  const updateOrderItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          
          // Recalculate pricing when material type or length changes
          if (field === 'materialTypeId' || field === 'length') {
            const unitPrice = calculateUnitPrice(updatedItem.materialTypeId, updatedItem.length);
            const totalPrice = calculateTotalPrice({ ...updatedItem, unitPrice });
            return {
              ...updatedItem,
              unitPrice: unitPrice,
              totalPrice: totalPrice
            };
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  // Calculate unit price based on material type and running length
  const calculateUnitPrice = (materialTypeId, length) => {
    const material = materialTypes.find(mt => mt.id === materialTypeId);
    if (!material || !length) return 0;
    
    // Unit price = price per m² * width in meters * length
    const widthInMeters = material.jumboWidth / 1000; // Convert mm to meters
    const unitPrice = material.pricePerSqm * widthInMeters * length;
    return unitPrice;
  };

  // Calculate total price for an item based on all its widths
  const calculateTotalPrice = (item) => {
    if (!item.unitPrice || !item.widths.length) return 0;
    
    return item.widths.reduce((total, width) => {
      const widthInMeters = (width.width || 0) / 1000;
      const quantity = width.quantity || 0;
      const length = item.length || 0;
      return total + (widthInMeters * length * quantity * item.unitPrice);
    }, 0);
  };

  // Auto-calculate pricing when material type or length changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map(item => {
        const unitPrice = calculateUnitPrice(item.materialTypeId, item.length);
        const totalPrice = calculateTotalPrice({ ...item, unitPrice });
        return {
          ...item,
          unitPrice: unitPrice,
          totalPrice: totalPrice
        };
      })
    }));
  }, [materialTypes]); // Recalculate when material types are loaded

  const addWidthBreakdown = (itemIndex) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map((item, i) => 
        i === itemIndex 
          ? { 
              ...item, 
              widths: [...item.widths, { width: '', quantity: 1, wtNumber: '' }] 
            }
          : item
      )
    }));
  };

  const removeWidthBreakdown = (itemIndex, widthIndex) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map((item, i) => 
        i === itemIndex 
          ? { 
              ...item, 
              widths: item.widths.filter((_, wi) => wi !== widthIndex) 
            }
          : item
      )
    }));
  };

  const updateWidthItem = (itemIndex, widthIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      orderItems: prev.orderItems.map((item, i) => {
        if (i === itemIndex) {
          const updatedItem = {
            ...item,
            widths: item.widths.map((width, wi) => 
              wi === widthIndex ? { ...width, [field]: value } : width
            )
          };
          
          // Recalculate total price when width or quantity changes
          if (field === 'width' || field === 'quantity') {
            const totalPrice = calculateTotalPrice(updatedItem);
            return {
              ...updatedItem,
              totalPrice: totalPrice
            };
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const getTotalWidthUsed = (item) => {
    return item.widths.reduce((total, width) => {
      return total + (parseInt(width.width || 0) * parseInt(width.quantity || 0));
    }, 0);
  };

  const getRemainingWidth = (item) => {
    const selectedMaterial = materialTypes.find(mt => mt.id === item.materialTypeId);
    if (!selectedMaterial) return 0;
    return selectedMaterial.jumboWidth - getTotalWidthUsed(item);
  };

  const calculateTotalOrderValue = () => {
    return formData.orderItems.reduce((total, item) => {
      return total + (parseFloat(item.totalPrice) || 0);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Transform data for API
      const orderData = {
        orderNumber: formData.orderNumber,
        supplierId: formData.supplierId,
        expectedDate: formData.expectedDate,
        notes: formData.notes,
        orderItems: formData.orderItems.flatMap(item => 
          item.widths.map(width => ({
            materialTypeId: item.materialTypeId,
            quantity: width.quantity,
            width: width.width,
            length: item.length,
            unitPrice: item.unitPrice,
            totalPrice: (width.width / 1000) * item.length * width.quantity * (item.unitPrice || 0),
            wtNumber: width.wtNumber
          }))
        )
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/material-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) {
        throw new Error('Failed to create material order');
      }

      const result = await response.json();
      
      if (onOrderAdded) {
        onOrderAdded(result);
      }
      
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content material-order-modal">
        <div className="modal-header">
          <h2>{editOrder ? 'Edit Material Order' : 'Create Material Order'}</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Basic Order Information - Top Row */}
          <div className="form-row">
            <div className="form-group">
              <label>Order Number *</label>
              <input
                type="text"
                value={formData.orderNumber}
                onChange={(e) => setFormData(prev => ({ ...prev, orderNumber: e.target.value }))}
                placeholder="PO-2024-001"
                required
              />
            </div>
            <div className="form-group">
              <label>Expected Delivery Date</label>
              <input
                type="date"
                value={formData.expectedDate}
                onChange={(e) => setFormData(prev => ({ ...prev, expectedDate: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Supplier *</label>
              <select
                value={formData.supplierId}
                onChange={(e) => {
                  setFormData(prev => ({ 
                    ...prev, 
                    supplierId: e.target.value,
                    orderItems: [] // Clear order items when supplier changes
                  }));
                }}
                required
              >
                <option value="">Select Supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>{supplier.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label>Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional order notes..."
              rows="3"
            />
          </div>

          {/* Order Items Section */}
          {formData.supplierId && (
            <div className="order-items-section">
              <div className="section-header">
                <h3>Order Items</h3>
                <button type="button" className="btn-secondary" onClick={addOrderItem}>
                  Add Material
                </button>
              </div>

              {formData.orderItems.map((item, index) => {
                const selectedMaterial = materialTypes.find(mt => mt.id === item.materialTypeId);
                const filteredMaterials = getFilteredMaterialTypes();
                
                return (
                  <div key={index} className="order-item">
                    <div className="order-item-header">
                      <span>Item {index + 1}</span>
                      <button 
                        type="button" 
                        className="btn-danger btn-small"
                        onClick={() => removeOrderItem(index)}
                      >
                        Remove
                      </button>
                    </div>
                    
                    {/* Material Information Row - Full Width */}
                    <div className="material-info-row">
                      <div className="form-group">
                        <label>Material Type *</label>
                        <select
                          value={item.materialTypeId}
                          onChange={(e) => updateOrderItem(index, 'materialTypeId', e.target.value)}
                          required
                        >
                          <option value="">Select Material Type</option>
                          {filteredMaterials.map(material => (
                            <option key={material.id} value={material.id}>
                              {material.productCode} - {material.topsheet} (Jumbo: {material.jumboWidth}mm)
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      <div className="form-group">
                        <label>Running Length (m) *</label>
                        <input
                          type="number"
                          min="1"
                          value={item.length}
                          onChange={(e) => updateOrderItem(index, 'length', parseInt(e.target.value))}
                          placeholder="e.g., 100"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Unit Price (R)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.unitPrice ? item.unitPrice.toFixed(2) : ''}
                          onChange={(e) => updateOrderItem(index, 'unitPrice', parseFloat(e.target.value))}
                          placeholder="Auto-calculated"
                        />
                      </div>

                      <div className="form-group">
                        <label>Total Price (R)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.totalPrice ? item.totalPrice.toFixed(2) : '0.00'}
                          readOnly
                          className="readonly-field"
                        />
                      </div>
                    </div>

                    {/* Width Breakdown - Each Width on Full Row */}
                    <div className="width-breakdown-section">
                      {item.widths.map((widthItem, widthIndex) => (
                        <div key={widthIndex} className="width-row">
                          <div className="width-row-header">
                            <span>Width {widthIndex + 1}</span>
                            {item.widths.length > 1 && (
                              <button 
                                type="button" 
                                className="btn-danger btn-small"
                                onClick={() => removeWidthBreakdown(index, widthIndex)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                          
                          <div className="width-fields">
                            <div className="form-group">
                              <label>Required Width (mm) *</label>
                              <input
                                type="number"
                                min="1"
                                max={selectedMaterial?.jumboWidth || 9999}
                                value={widthItem.width}
                                onChange={(e) => updateWidthItem(index, widthIndex, 'width', parseInt(e.target.value))}
                                placeholder="e.g., 200"
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>Number of Rolls *</label>
                              <input
                                type="number"
                                min="1"
                                value={widthItem.quantity}
                                onChange={(e) => updateWidthItem(index, widthIndex, 'quantity', parseInt(e.target.value))}
                                required
                              />
                            </div>
                            <div className="form-group">
                              <label>WT Number</label>
                              <input
                                type="text"
                                value={widthItem.wtNumber}
                                onChange={(e) => updateWidthItem(index, widthIndex, 'wtNumber', e.target.value)}
                                placeholder="WT12345"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <button 
                        type="button" 
                        className="btn-secondary btn-small add-width-btn" 
                        onClick={() => addWidthBreakdown(index)}
                      >
                        Add New Width
                      </button>
                    </div>

                    {/* Width Usage Progress Bar - Full Width at Bottom */}
                    {selectedMaterial && (
                      <div className="width-usage-section">
                        <div className="width-usage-header">
                          <span className="width-label">Width Usage: {getTotalWidthUsed(item)}mm / {selectedMaterial.jumboWidth}mm</span>
                        </div>
                        <div className="width-progress-bar">
                          <div 
                            className="width-used" 
                            style={{ 
                              width: `${Math.min((getTotalWidthUsed(item) / selectedMaterial.jumboWidth) * 100, 100)}%` 
                            }}
                          ></div>
                        </div>
                        <div className="width-legend">
                          <div className="legend-item">
                            <div className="legend-color used"></div>
                            <span>Used Width</span>
                          </div>
                          <div className="legend-item">
                            <div className="legend-color available"></div>
                            <span>Available Width</span>
                          </div>
                        </div>
                        
                        <div className="width-stats">
                          <div className="width-stat">
                            <span className="label">Total Width Used:</span>
                            <span className="value">{getTotalWidthUsed(item)}mm</span>
                          </div>
                          <div className="width-stat">
                            <span className="label">Remaining Width:</span>
                            <span className={`value ${getRemainingWidth(item) < 0 ? 'error' : 'success'}`}>
                              {getRemainingWidth(item)}mm
                            </span>
                          </div>
                        </div>
                        
                        {getRemainingWidth(item) < 0 && (
                          <div className="error-message">
                            Width exceeds jumbo roll capacity!
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {formData.orderItems.length === 0 && (
                <div className="info-message">
                  <p>Please select a supplier first to add order items.</p>
                </div>
              )}
            </div>
          )}

          {/* Order Summary */}
          {formData.orderItems.length > 0 && (
            <div className="order-summary">
              <div className="total-value">
                <strong>Total Order Value: R{calculateTotalOrderValue().toFixed(2)}</strong>
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : (editOrder ? 'Update Order' : 'Create Order')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMaterialOrderModal;
